import React, { useState, useEffect, useRef } from 'react';
import {
	Grid,
	Typography,
	Button,
	Box,
	Container,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	useTheme,
	useMediaQuery,
	Dialog,
	DialogContent,
} from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import QuoteForm from './QuoteForm';

const WhyChooseUs = () => {
	const controls = useAnimation();
	const sectionRef = useRef(null);
	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.down('sm'));
	const [isInView, setIsInView] = useState(false);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				setIsInView(entry.isIntersecting);
			},
			{ threshold: 0.2 } // Adjust threshold as needed
		);

		if (sectionRef.current) observer.observe(sectionRef.current);

		return () => {
			if (sectionRef.current) observer.unobserve(sectionRef.current);
		};
	}, []);

	useEffect(() => {
		if (isInView) {
			controls.start('visible');
		}
	}, [isInView, controls]);

	const containerVariants = {
		hidden: { opacity: 0, x: '-100vw' },
		visible: { opacity: 1, x: 0, transition: { duration: 1, ease: 'easeOut' } },
	};

	const featureVariants = {
		hidden: { opacity: 0, x: '100vw' },
		visible: (index) => ({
			opacity: 1,
			x: 0,
			transition: {
				duration: 1,
				delay: index * 0.2,
				ease: 'easeOut',
			},
		}),
	};
	const [open, setOpen] = useState(false);

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};
	return (
		<Box
			// component='section'
			ref={sectionRef}
			sx={{
				backgroundColor: '#f9f9f9',
				padding: matches ? '' : 20,
				marginTop: matches ? 10 : '',
			}}>
			<Container>
				<Grid container spacing={4} alignItems='center'>
					{/* Left Column */}
					<Grid item xs={12} md={4}>
						<motion.div
							variants={containerVariants}
							initial='hidden'
							animate={controls}>
							<Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
								<Typography
									variant='h5'
									gutterBottom
									sx={{ fontWeight: 'bold' }}>
									Why Choose Us?
								</Typography>
								<Typography variant='body1' sx={{ mb: 3 }}>
									ZOODONIX . is a reputable development company
									that offers quality services to its worldwide clientele. We
									are a one-stop development service provider for clients in
									different business domains.
								</Typography>
								<Button

									sx={{ background: 'linear-gradient(to right, #2D0B5A, #C106A3)' }}
									variant='contained'
									color='primary'
									onClick={handleClickOpen}>
									Talk To Experts Now
								</Button>
							</Box>
						</motion.div>
					</Grid>

					{/* Right Column */}
					<Grid item xs={12} md={8}>
						<List disablePadding>
							<Grid container spacing={2}>
								{features.map((feature, index) => (
									<Grid item xs={12} sm={6} key={index}>
										<motion.div
											variants={featureVariants}
											initial='hidden'
											animate={controls}
											custom={index}>
											<ListItem
												sx={{
													display: 'flex',
													alignItems: 'flex-start',
													background: 'white',
													padding: '30px',
													boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
													borderRadius: '8px',
												}}>
												<ListItemIcon>
													<img
														src={feature.icon}
														alt={feature.altText}
														style={{ width: 40, height: 40 }}
													/>
												</ListItemIcon>
												<ListItemText
													primary={
														<Typography
															variant='h6'
															sx={{ fontWeight: 'bold' }}>
															{feature.title}
														</Typography>
													}
													secondary={
														<Typography
															variant='body2'
															sx={{ color: 'text.secondary' }}>
															{feature.description}
														</Typography>
													}
												/>
											</ListItem>
										</motion.div>
									</Grid>
								))}
							</Grid>
						</List>
					</Grid>
				</Grid>
			</Container>
			{/* Form Modal */}
			<Dialog open={open} onClose={handleClose} maxWidth="md">
				<DialogContent>
					<QuoteForm />
				</DialogContent>
			</Dialog>
		</Box>

	);
};

const features = [
	{
		icon: 'https://suffescom.co/assets/images/expert-team.svg',
		altText: 'Expert Team',
		title: 'Expert Team',
		description:
			'We leverage our domain expertise to apply the latest technology to solve complex business challenges.',
	},
	{
		icon: 'https://suffescom.co/assets/images/transparent-process.svg',
		altText: 'Transparent Process',
		title: 'Transparent Process',
		description:
			'We follow a transparent development process, where the information related to your project is shared with you.',
	},
	{
		icon: 'https://suffescom.co/assets/images/global-network.svg',
		altText: 'Multi-Domain',
		title: 'Multi-Domain',
		description:
			'We extend our multi-domain expertise to businesses worldwide looking for custom development services.',
	},
	{
		icon: 'https://suffescom.co/assets/images/on-time-results.svg',
		altText: 'On-Time Delivery',
		title: 'On-Time Delivery',
		description:
			'We ensure to complete and deliver the project on time with complete accuracy and zero technical issues or bugs.',
	},
	{
		icon: 'https://suffescom.co/assets/images/mobile-app-development-end-to-end-services.svg',
		altText: 'End-to-End Services',
		title: 'End-to-End Services',
		description:
			'To ensure quality output, we deliver services that include development, testing, and post-deployment support.',
	},
	{
		icon: 'https://suffescom.co/assets/images/custom-software-development-support.svg',
		altText: '24x7 Support',
		title: '24x7 Support',
		description:
			'Our technical support team is available 24/7 to resolve all your queries and provide technical assistance.',
	},
];

export default WhyChooseUs;
